/**
 * PlatformBootstrapProvider — loads platform config from Bootstrap API.
 *
 * Replaces all VITE_* build-time env vars with runtime config from
 * GET /api/v1/platform/bootstrap (relative URL — works via Vite proxy or nginx proxy).
 *
 * Usage:
 *   <PlatformBootstrapProvider>
 *     <App />
 *   </PlatformBootstrapProvider>
 *
 * Hooks:
 *   usePlatformConfig()  — full config object
 *   useOAuthConfig()     — { googleClientId, facebookAppId }
 *   useWsUrl()           — WebSocket URL string
 *
 * Module-level (for non-React code like images.ts):
 *   getS3ProductsUrl(), getS3LogosUrl(), getS3AvatarsUrl(), getWsUrl(), getGatewayUrl()
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Center, Loader, Stack, Text, Button } from '@mantine/core';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PlatformBootstrapConfig {
  oauth: {
    googleClientId: string;
    facebookAppId: string;
  };
  urls: {
    gateway: string;
    ws: string;
    s3Products: string;
    s3Logos: string;
    s3Avatars: string;
    platform: string;
    authPortal: string;
    merchantDomainPrefix: string;
    baseDomain: string;
  };
  features: {
    socialLogin: boolean;
    telegramAuth: boolean;
    googleAuth: boolean;
    facebookAuth: boolean;
  };
  meta: {
    appId: string;
    embeddedSignupConfigId: string;
    graphApiVersion: string;
  };
}

interface PlatformBootstrapState {
  config: PlatformBootstrapConfig | null;
  isLoading: boolean;
  error: Error | null;
}

// ─── Module-level singleton ──────────────────────────────────────────────────

let _config: PlatformBootstrapConfig | null = null;

export function getS3ProductsUrl(): string {
  return _config?.urls.s3Products ?? 'https://s3.teletienda.ar/teletienda-products';
}

export function getS3LogosUrl(): string {
  return _config?.urls.s3Logos ?? 'https://s3.teletienda.ar/merchant-logos';
}

export function getS3AvatarsUrl(): string {
  return _config?.urls.s3Avatars ?? 'https://s3.teletienda.ar/user-avatars';
}

export function getWsUrl(): string {
  return _config?.urls.ws ?? '';
}

export function getGatewayUrl(): string {
  return _config?.urls.gateway ?? '';
}

export function getMerchantDomainPrefix(): string {
  return _config?.urls.merchantDomainPrefix ?? '';
}

export function getBaseDomain(): string {
  return _config?.urls.baseDomain ?? 'teletienda.ar';
}

// ─── Context ─────────────────────────────────────────────────────────────────

const PlatformBootstrapContext = createContext<PlatformBootstrapState>({
  config: null,
  isLoading: true,
  error: null,
});

// ─── Provider ────────────────────────────────────────────────────────────────

export function PlatformBootstrapProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlatformBootstrapState>({
    config: null,
    isLoading: true,
    error: null,
  });

  const fetchBootstrap = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const res = await fetch('/api/v1/platform/bootstrap');
      if (!res.ok) throw new Error(`Bootstrap API returned ${res.status}`);
      const json = await res.json();
      const config: PlatformBootstrapConfig = json.data ?? json;

      _config = config; // set module-level singleton
      setState({ config, isLoading: false, error: null });
    } catch (err) {
      console.error('[PlatformBootstrap] Failed to load config:', err);
      setState({ config: null, isLoading: false, error: err as Error });
    }
  };

  useEffect(() => {
    fetchBootstrap();
  }, []);

  if (state.isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading platform...</Text>
        </Stack>
      </Center>
    );
  }

  if (state.error) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text c="red" fw={600}>Failed to load platform configuration</Text>
          <Text c="dimmed" size="sm">{state.error.message}</Text>
          <Button onClick={fetchBootstrap} variant="outline">Retry</Button>
        </Stack>
      </Center>
    );
  }

  return (
    <PlatformBootstrapContext.Provider value={state}>
      {children}
    </PlatformBootstrapContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function usePlatformConfig(): PlatformBootstrapConfig {
  const { config } = useContext(PlatformBootstrapContext);
  if (!config) throw new Error('usePlatformConfig must be used within PlatformBootstrapProvider');
  return config;
}

export function useOAuthConfig(): PlatformBootstrapConfig['oauth'] {
  return usePlatformConfig().oauth;
}

export function useS3Urls(): PlatformBootstrapConfig['urls'] {
  return usePlatformConfig().urls;
}

export function useWsUrl(): string {
  return usePlatformConfig().urls.ws;
}

export function usePlatformFeatures(): PlatformBootstrapConfig['features'] {
  return usePlatformConfig().features;
}

export function useMetaConfig(): PlatformBootstrapConfig['meta'] {
  return usePlatformConfig().meta;
}
